import React, { ReactElement, useState } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import AuthRepository from "../../repositories/AuthRepository";
import { useAuth } from "../../contexts/AuthContext";
import { AuthorizationResult } from "../../models/AuthorizationResult";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router";

import { dialogNarrow } from "@/lib/dialogSize";

interface GetDocumentsTemporaryAccessModalProps {
  children: ReactElement;
}

const GetDocumentsTemporaryAccessModal: React.FC<
  GetDocumentsTemporaryAccessModalProps
> = ({ children }) => {
  const authRepository = new AuthRepository();
  const { refreshToken, login } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRequestAccess = async () => {
    if (!reason.trim()) return;

    setIsSubmitting(true);
    try {
      try {
        await authRepository.getTemporaryAccess(reason);
      } catch (error) {
        // The dialog stays open with the reason kept, so the user can read why and try again.
        toast.error(error instanceof Error ? error.message : "Failed to get temporary access.");
        return;
      }
      try {
        // The grant is carried by the sign-in, so a new token pair is needed before the access takes effect.
        const apiUrl = import.meta.env.VITE_API_URL ?? "/";
        const api = axios.create({ baseURL: apiUrl, timeout: 30000 });
        const response = await api.post(`api/RefreshToken`, {
          refreshToken: refreshToken,
        });
        const tokens = response.data as AuthorizationResult;
        login(tokens.accessToken, tokens.refreshToken);
      } catch (error) {
        alert("Session expired!");
        navigate("/login");
        console.error("Failed to refresh access token:", error);
      }
      setOpen(false);
      setReason("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setReason("");
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); else setOpen(true); }}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className={`${dialogNarrow} overflow-hidden`}>
          <DialogHeader>
            <DialogTitle>
              Temporary Access To Documents Request
            </DialogTitle>
          </DialogHeader>

          <Separator />

          <div className="mt-1 space-y-2">
            <Label>Temporary Access To Documents Reason:</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please enter the reason for requesting temporary access to documents of this organization..."
              rows={4}
              className="max-h-[50vh] resize-y"
              required
            />
          </div>

          <DialogFooter className="mt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestAccess}
              disabled={!reason.trim() || isSubmitting}
              className="min-w-[100px]"
            >
              {isSubmitting ? "Requesting..." : "Get Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  );
};

export default GetDocumentsTemporaryAccessModal;
